// Root error boundary (ticket 123). React error boundaries must be classes.
// Reports to Sentry (when enabled) and offers Reload / Send feedback instead
// of a blank white screen. `inline` renders a smaller, non-full-viewport
// fallback for the boundary around PortalLayout's <Outlet /> so the top nav
// survives a page crash.
import React from 'react';
import { RefreshCw, MessageSquare } from 'lucide-react';
import { captureException, getLastRequestId } from '../observability';
import FeedbackDialog from '../portal/FeedbackDialog';

export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, eventId: null, feedbackOpen: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    const eventId = captureException(error, { contexts: { react: { componentStack: info?.componentStack } } });
    this.setState({ eventId });
  }

  // A route change (resetKey = location.pathname from the caller) past an
  // error clears it instead of remounting the whole subtree — the boundary
  // recovers, the crashed page's own component state does not carry over.
  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, eventId: null, feedbackOpen: false });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const { inline } = this.props;
    const reference = this.state.eventId || getLastRequestId();

    return (
      <div
        data-testid="app-error-fallback"
        className={inline ? 'w-full p-10' : 'min-h-screen w-full flex items-center justify-center p-6'}
      >
        <div className="max-w-md w-full text-center">
          <div
            className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full"
            style={{ background: '#070B5F15', color: '#070B5F' }}
          >
            <RefreshCw size={22} />
          </div>
          <h1 className="font-serif text-2xl font-bold" style={{ color: '#070B5F' }}>
            Something went wrong
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            The page hit an error. Reload to try again, or send us a note so we can look into it.
          </p>
          <div className="mt-5 flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              data-testid="app-error-reload"
              className="text-[13px] font-semibold px-4 py-2 rounded-lg text-white flex items-center gap-1.5 shadow-md hover:brightness-105"
              style={{ background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' }}
            >
              <RefreshCw size={13} /> Reload
            </button>
            <button
              type="button"
              onClick={() => this.setState({ feedbackOpen: true })}
              data-testid="app-error-feedback"
              className="text-[13px] font-semibold px-4 py-2 rounded-lg border border-slate-200 text-slate-700 flex items-center gap-1.5 hover:bg-slate-50"
            >
              <MessageSquare size={13} /> Send feedback
            </button>
          </div>
          {reference && (
            <div className="mt-4 text-[11px] font-mono text-slate-400">Reference: {reference}</div>
          )}
        </div>
        <FeedbackDialog
          open={this.state.feedbackOpen}
          onOpenChange={(open) => this.setState({ feedbackOpen: open })}
          sentryEventId={this.state.eventId}
          page={window.location.pathname}
        />
      </div>
    );
  }
}
